import type { TurnWorld, Unit, Building, UnitType } from './types';
import { resolveBaseType } from './units';

/** Unit types that can capture buildings. */
const CAPTURE_TYPES: ReadonlySet<UnitType> = new Set<UnitType>([
  'infantry', 'infantry-bazooka', 'cavalry',
]);

export function canUnitCapture(unit: Unit): boolean {
  return CAPTURE_TYPES.has(unit.type) || CAPTURE_TYPES.has(resolveBaseType(unit.type));
}

/**
 * Check if a unit can perform the capture action.
 * Only capture-capable units on a building tile that isn't already owned by the unit's owner.
 */
export function canCapture(world: TurnWorld, unit: Unit): Building | null {
  if (!canUnitCapture(unit)) return null;
  if (unit.acted) return null;

  for (const [, b] of world.buildings) {
    if (b.col === unit.col && b.row === unit.row && b.owner !== unit.owner) {
      // Bunkers cannot be captured — they are always neutral
      if (b.type === 'bunker') continue;
      return b;
    }
  }
  return null;
}

/**
 * Execute capture action: unit captures a building.
 * Returns true if ownership transferred (capture completed).
 */
export function executeCapture(_world: TurnWorld, unit: Unit, building: Building): boolean {
  if (!canUnitCapture(unit)) return false;
  if (building.col !== unit.col || building.row !== unit.row) return false;

  // If a different player was capturing, reset and start fresh
  if (building.capturingPlayer !== null && building.capturingPlayer !== unit.owner) {
    building.captureProgress = 0;
  }

  building.capturingPlayer = unit.owner;
  building.captureProgress += 1;
  unit.acted = true;

  if (building.captureProgress >= 2) {
    // Capture complete — transfer ownership
    building.owner = unit.owner;
    building.captureProgress = 0;
    building.capturingPlayer = null;
    // Cancel any training from previous owner
    building.training = null;
    return true;
  }

  return false;
}

/**
 * Reset capture progress on a building.
 * Called when the capturing unit leaves or dies.
 */
export function resetCapture(building: Building): void {
  building.captureProgress = 0;
  building.capturingPlayer = null;
}

/**
 * Check all buildings: if a building is being captured but the capturing
 * unit is no longer on it, reset the capture progress.
 */
export function validateCaptures(world: TurnWorld): void {
  for (const [, building] of world.buildings) {
    if (building.capturingPlayer === null) continue;

    // Find the capturing unit on this tile
    let found = false;
    for (const [, unit] of world.units) {
      if (
        canUnitCapture(unit) &&
        unit.owner === building.capturingPlayer &&
        unit.col === building.col &&
        unit.row === building.row
      ) {
        found = true;
        break;
      }
    }

    if (!found) {
      resetCapture(building);
    }
  }
}

/**
 * Find building at given coordinates.
 */
export function getBuildingAt(world: TurnWorld, col: number, row: number): Building | null {
  for (const [, b] of world.buildings) {
    if (b.col === col && b.row === row) return b;
  }
  return null;
}
